<template>
  <app-custom-dialog
    v-model="show"
    :width="'1300px'"
    :footer="false"
    title="shift_details"
    flex-layout
    dialog-height="85vh"
    v-if="shift"
  >
    <div slot="activator" class="shift-detail">
      <div class="detail-header">
        <div>
          <div class="detail-header-name">
            {{
              [shift.user.first_name, shift.user.last_name]
                .filter(Boolean)
                .join(" ") || "-"
            }}
          </div>
          <div v-if="shift.branch" class="detail-header-sub">
            {{ shift.branch.name }}
          </div>
        </div>
        <ShiftStatusBadge :status="shift.status" />
      </div>

      <div class="detail-grid">
        <div v-if="shift.cash_drawer" class="detail-item">
          <span class="detail-label">{{ $t("cash_drawer_name") }}</span>
          <span class="detail-value">{{ shift.cash_drawer.name }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">{{ $t("clock_in") }}</span>
          <span class="detail-value">{{ displayShift.clock_in_at | moment }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">{{ $t("clock_out") }}</span>
          <span class="detail-value">{{
            displayShift.clock_out_at ? $options.filters.moment(displayShift.clock_out_at) : "-"
          }}</span>
        </div>
        <div v-if="shift.approved_by" class="detail-item">
          <span class="detail-label">{{ $t("approved_by") }}</span>
          <span class="detail-value">
            {{
              [shift.approved_by.first_name, shift.approved_by.last_name]
                .filter(Boolean)
                .join(" ") || "-"
            }}
          </span>
        </div>
        <div v-if="shift.approved_at" class="detail-item">
          <span class="detail-label">{{ $t("approved_at") }}</span>
          <span class="detail-value">{{ shift.approved_at | moment }}</span>
        </div>
        <div v-if="shift.notes" class="detail-item detail-item--full">
          <span class="detail-label">{{ $t("notes") }}</span>
          <span class="detail-value">{{ shift.notes }}</span>
        </div>
      </div>

      <div class="detail-section-title">{{ $t("cash_transactions") }}</div>
      <div class="detail-stats-row">
        <div class="detail-stat">
          <div class="detail-stat-value">{{ shift.opening_cash | NumberFormat }}</div>
          <div class="detail-stat-label">{{ $t("opening_cash") }}</div>
        </div>
        <div class="detail-stat">
          <div class="detail-stat-value">{{ statValue(shift.closing_cash) }}</div>
          <div class="detail-stat-label">{{ $t("closing_cash") }}</div>
        </div>
        <div class="detail-stat">
          <!-- `total_sales` counts cash only (Shift::calculateTotals sums
               `cash` tickets plus the cash leg of `cash_transfer`), because
               it is the base of expected_cash. Labelling it "total sales"
               next to a list that shows QR and transfer sales too invited
               the reading that the two should add up. -->
          <div class="detail-stat-value">{{ statValue(shift.total_sales) }}</div>
          <div class="detail-stat-label">{{ $t("cash_sales") }}</div>
        </div>
        <div class="detail-stat">
          <div class="detail-stat-value">{{ statValue(expectedCash) }}</div>
          <div class="detail-stat-label">{{ $t("expected_cash") }}</div>
        </div>
        <div class="detail-stat" :class="varianceClass">
          <div class="detail-stat-value">{{ statValue(shift.variance) }}</div>
          <div class="detail-stat-label">{{ $t("variance") }}</div>
        </div>
      </div>

      <!-- Transactions -->
      <div class="detail-section-title">{{ $t("transactions") }}</div>
      <div class="segmented-tabs">
        <button
          type="button"
          class="segmented-tab"
          :class="{ 'segmented-tab--active': activeTab === 0 }"
          @click="activeTab = 0"
        >
          {{ $t("sales") }}
        </button>
        <button
          type="button"
          class="segmented-tab"
          :class="{ 'segmented-tab--active': activeTab === 1 }"
          @click="activeTab = 1"
        >
          {{ $t("cash_transactions") }}
        </button>
      </div>

      <v-tabs-items v-model="activeTab" class="transactions-panel">
        <v-tab-item>
          <v-skeleton-loader
            v-if="loadingTransactions"
            type="table-thead, table-tbody"
          />
          <div v-else-if="!shiftTransactions.length" class="detail-empty detail-empty--block">
            {{ $t("no_data") }}
          </div>
          <v-data-table
            v-else
            :headers="saleHeaders"
            :items="shiftTransactions"
            :items-per-page="30"
            :footer-props="{ itemsPerPageOptions: [30, 50, 100, -1] }"
            :expanded.sync="expandedSaleRows"
            show-expand
            single-expand
            item-key="id"
            dense
            class="transactions-table"
          >
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.amount="{ item }">
              {{ formatLak(item.amount) }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.created_at="{ item }">
              {{ item.created_at | moment }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.items="{ item }">
              <span class="sale-items-count">
                {{ (item.items || []).length }} {{ $t("items") }}
              </span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="sale-items-cell">
                <div v-if="isForeignCurrency(item)" class="sale-currency-note">
                  {{ $t("exchange_rate") }}: 1 {{ item.currency_code }} =
                  {{ item.exchange_rate | NumberFormat }} ₭
                </div>
                <div v-if="!(item.items || []).length" class="detail-empty">
                  {{ $t("no_data") }}
                </div>
                <table v-else class="sale-items-table">
                  <thead>
                    <tr>
                      <th scope="col">{{ $t("product") }}</th>
                      <th scope="col" class="sale-items-num">{{ $t("qty") }}</th>
                      <th scope="col" class="sale-items-num">{{ $t("price_sale") }}</th>
                      <th scope="col" class="sale-items-num">{{ $t("total_price") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, idx) in item.items" :key="idx">
                      <td>{{ line.product_name }}</td>
                      <td class="sale-items-num">{{ line.qty }}</td>
                      <td class="sale-items-num">{{ formatLak(line.price_sale) }}</td>
                      <td class="sale-items-num">{{ formatLak(line.total_price) }}</td>
                    </tr>
                  </tbody>
                  <!-- item.total_price, item.amount, and this whole breakdown
                       are always stored (and shown here) in the store's base
                       currency (LAK) — matching the listed sale price rather
                       than the currency the customer paid in. -->
                  <tfoot>
                    <tr class="sale-items-summary-row">
                      <td colspan="3">{{ $t("subtotal") }}</td>
                      <td class="sale-items-num">{{ formatLak(itemsSubtotal(item)) }}</td>
                    </tr>
                    <tr v-if="item.total_discount" class="sale-items-summary-row">
                      <td colspan="3">{{ $t("discount") }}</td>
                      <td class="sale-items-num">-{{ formatLak(item.total_discount) }}</td>
                    </tr>
                    <tr v-if="item.tax_amount" class="sale-items-summary-row">
                      <td colspan="3">{{ $t("tax") }}</td>
                      <td class="sale-items-num">{{ formatLak(item.tax_amount) }}</td>
                    </tr>
                    <tr class="sale-items-summary-row sale-items-summary-row--total">
                      <td colspan="3">{{ $t("total") }}</td>
                      <td class="sale-items-num">{{ formatLak(item.amount) }}</td>
                    </tr>
                    <!-- THB/USD equivalents use the store's general exchange
                         rate, independent of the sale's own payment currency,
                         so they show up even for a LAK or USD sale. -->
                    <tr class="sale-items-summary-row">
                      <td colspan="3">THB</td>
                      <td class="sale-items-num">{{ formatForeign(item.amount, "THB") }}</td>
                    </tr>
                    <tr class="sale-items-summary-row">
                      <td colspan="3">USD</td>
                      <td class="sale-items-num">{{ formatForeign(item.amount, "USD") }}</td>
                    </tr>
                    <tr class="sale-items-summary-row sale-items-summary-row--total">
                      <td colspan="3">{{ $t("paid_amount") }}</td>
                      <td class="sale-items-num">{{ formatSaleAmount(item, item.amount) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </td>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <v-skeleton-loader
            v-if="loadingCashTransactions && !hasLoadedCash"
            type="table-thead, table-tbody"
          />
          <div
            v-else-if="!loadingCashTransactions && !cashTransactions.length"
            class="detail-empty detail-empty--block"
          >
            {{ $t("no_data") }}
          </div>
          <v-data-table
            v-else
            :headers="cashHeaders"
            :items="cashTransactions"
            :options.sync="cashOptions"
            :server-items-length="cashTotal"
            :loading="loadingCashTransactions"
            :footer-props="{ itemsPerPageOptions: [30, 50, 100, -1] }"
            dense
            class="transactions-table"
          >
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.no="{ index }">
              {{ (cashOptions.page - 1) * cashOptions.itemsPerPage + index + 1 }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.amount="{ item }">
              <span
                :class="item.type === 'cash_in' ? 'green--text' : 'red--text'"
              >
                {{ item.amount | NumberFormat }}
              </span>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.created_at="{ item }">
              {{ item.created_at | moment }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.type="{ item }">
              <v-chip
                :color="item.type === 'cash_in' ? 'green' : 'red'"
                dark
                x-small
              >
                {{ $t(item.type) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </app-custom-dialog>
</template>

<script>
import ShiftStatusBadge from "@/components/shift/ShiftStatusBadge";
import customDialog from "@/components/customDialog.vue";
import { myMixin } from "@/mixin/main";
import {
  shiftStatValue,
  shiftVarianceClass,
} from "@/utils/shiftDisplay";
export default {
  name: "ShiftDetailDialog",
  components: {
    ShiftStatusBadge,
    "app-custom-dialog": customDialog,
  },
  mixins: [myMixin],
  props: {
    value: Boolean,
    shift: {
      type: Object,
      default: () => ({
        user: {
          first_name: "",
          last_name: "",
        },
        branch: {
          name: "",
        },
        clock_in_at: "",
        clock_out_at: "",
        opening_cash: 0,
        closing_cash: 0,
        total_sales: 0,
        cash_in_total: 0,
        cash_out_total: 0,
        variance: 0,
        notes: "",
        approved_by: {
          first_name: "",
          last_name: "",
        },
        approved_at: "",
        status: "",
      }),
    },
  },
  data: () => ({
    activeTab: 0,
    expandedSaleRows: [],
    shiftTransactions: [],
    cashTransactions: [],
    // Authoritative clock_in_at/clock_out_at fetched fresh alongside the
    // transactions list — the `shift` prop can be a stale row from a list
    // fetched before this shift was clocked out, so this backfills the
    // header display once the dialog opens instead of trusting the prop.
    freshShiftTimes: null,
    loadingTransactions: false,
    loadingCashTransactions: false,
    cashOptions: { page: 1, itemsPerPage: 30 },
    cashTotal: 0,
    // True once the cash table has been fetched at least once. Keeps the
    // v-data-table mounted (instead of v-if-toggled away) on every later
    // page/per-page fetch — see the note above the cashOptions watcher for
    // why an unmount/remount here caused a fetch loop.
    hasLoadedCash: false,
    // Set right before we reset cashOptions ourselves (opening the dialog)
    // so the v-data-table footer's own options watcher doesn't fire a
    // second, redundant fetch on top of the one we just triggered.
    suppressCashOptionsWatch: false,
    // LAK-per-1-unit rate for each currency code (e.g. { THB: 680, USD: 22500 }),
    // used to show the THB/USD equivalent of a sale regardless of what
    // currency it was actually paid in.
    currencyRates: {},
  }),
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    displayShift() {
      if (!this.freshShiftTimes) return this.shift;
      return { ...this.shift, ...this.freshShiftTimes };
    },
    expectedCash() {
      if (!this.shift) return 0;
      const opening = Number.parseFloat(this.shift.opening_cash) || 0;
      const sales = Number.parseFloat(this.shift.total_sales) || 0;
      const cashIn = Number.parseFloat(this.shift.cash_in_total) || 0;
      const cashOut = Number.parseFloat(this.shift.cash_out_total) || 0;
      return opening + sales + cashIn - cashOut;
    },
    varianceClass() {
      return shiftVarianceClass(this.shift && this.shift.variance);
    },
    saleHeaders() {
      return [
        { text: this.$t("id"), value: "code" },
        { text: this.$t("time"), value: "created_at" },
        { text: this.$t("items"), value: "items", sortable: false },
        { text: this.$t("sale"), value: "amount" },
        { text: this.$t("payment_method"), value: "payment_method" },
      ];
    },
    cashHeaders() {
      return [
        { text: this.$t("no_sequence"), value: "no", sortable: false },
        { text: this.$t("time"), value: "created_at" },
        { text: this.$t("type"), value: "type" },
        { text: this.$t("amount"), value: "amount" },
        { text: this.$t("reason"), value: "reason" },
      ];
    },
  },
  methods: {
    statValue(value) {
      return shiftStatValue(value, this.$options.filters.NumberFormat);
    },
    // 'include': each line's total_price already contains VAT (mirrors
    // backend VatCalculator), so strip the ticket's tax_amount out for this
    // to read as a true pre-VAT subtotal; 'exclude' lines never had VAT in
    // them, so the raw sum is already correct.
    itemsSubtotal(item) {
      const raw = (item.items || []).reduce(
        (sum, line) => sum + (Number(line.total_price) || 0),
        0
      );
      if (item.vat_mode === "include") {
        return Math.max(raw - (Number(item.tax_amount) || 0), 0);
      }
      return raw;
    },
    isForeignCurrency(item) {
      return !!item.currency_code && item.currency_code !== "LAK";
    },
    // LAK sales still carry currency_code/currency_symbol from the backend
    // (just "LAK"/"₭") — show that too instead of only tagging foreign
    // currencies, so every sale is labelled consistently.
    currencyLabel(item) {
      // Tickets predating currency tracking (currency_id null) fall back to
      // "LAK" with no symbol from the backend — show the Kip sign for those
      // too instead of the bare code.
      if (item.currency_symbol) return item.currency_symbol;
      if (!item.currency_code || item.currency_code === "LAK") return "₭";
      return item.currency_code;
    },
    /**
     * Every sale figure (price_sale/total_price/discount/tax/amount) is
     * always stored in the store's base currency (LAK) — for a LAK sale
     * this is a no-op (exchange_rate is 1), for a foreign-currency sale
     * (e.g. THB) it converts LAK -> that currency.
     */
    formatSaleAmount(item, value) {
      const rate = Number(item.exchange_rate) || 1;
      const converted = Number(value || 0) / rate;
      const formatted = this.$options.filters.NumberFormat(converted);
      return `${formatted} ${this.currencyLabel(item)}`;
    },
    // Every sale figure is stored in LAK — this just displays it as such,
    // regardless of the currency the sale was paid in.
    formatLak(value) {
      return `${this.$options.filters.NumberFormat(value || 0)} ₭`;
    },
    // Converts a LAK amount into another currency using the store's general
    // exchange rate (not the sale's own currency/rate), so this works even
    // for sales that were paid in a different currency, or in LAK.
    formatForeign(value, code) {
      const rate = this.currencyRates[code];
      if (!rate) return "-";
      const converted = Number(value || 0) / rate;
      const symbol = code === "THB" ? "฿" : code === "USD" ? "$" : code;
      return `${this.$options.filters.NumberFormat(converted)} ${symbol}`;
    },
    async fetchCurrencyRates() {
      try {
        const { data } = await this.$http.get("get_all_currencies");
        if (data && data.status && Array.isArray(data.data)) {
          const rates = {};
          data.data.forEach((currency) => {
            const rate = currency.store_exchange_rate ?? currency.exchange_rate;
            if (currency.code && Number(rate) > 0) {
              rates[currency.code] = Number(rate);
            }
          });
          this.currencyRates = rates;
        }
      } catch (error) {
        console.error("Error fetching currency rates:", error);
      }
    },
    async fetchShiftTransactions() {
      if (!this.shift) return;
      this.loadingTransactions = true;
      this.expandedSaleRows = [];
      try {
        const { data } = await this.$http.get(
          `shifts/${this.shift.id}/transactions`
        );
        if (data.status) {
          this.shiftTransactions = data.data.transactions.filter(
            (t) => t.transaction_type === "sale"
          );
          this.freshShiftTimes = data.data.shift || null;
        }
      } catch (error) {
        console.error("Error fetching shift transactions:", error);
      } finally {
        this.loadingTransactions = false;
      }
    },
    async fetchCashTransactions() {
      if (!this.shift) return;
      this.loadingCashTransactions = true;
      try {
        const { data } = await this.$http.get("cash_transaction_paginate", {
          params: {
            shift_id: this.shift.id,
            page: this.cashOptions.page,
            per_page: this.cashOptions.itemsPerPage,
          },
        });
        if (data.status) {
          this.cashTransactions = data.data.data || [];
          this.cashTotal = data.data.total || 0;
        }
      } catch (error) {
        console.error("Error fetching cash transactions:", error);
      } finally {
        this.loadingCashTransactions = false;
        this.hasLoadedCash = true;
      }
    },
  },
  created() {
    this.fetchCurrencyRates();
  },
  watch: {
    value(val) {
      if (val && this.shift) {
        this.freshShiftTimes = null;
        this.fetchShiftTransactions();
        // Reset to page 1 for a freshly opened shift. Setting
        // suppressCashOptionsWatch first stops the cashOptions watcher below
        // from firing its own duplicate fetch for this reset.
        this.suppressCashOptionsWatch = true;
        this.hasLoadedCash = false;
        this.cashOptions = { page: 1, itemsPerPage: this.cashOptions.itemsPerPage };
        this.fetchCashTransactions();
      }
    },
    // BUG THIS FIXES: the cash table used to be v-if/v-else-toggled away
    // whenever loadingCashTransactions was true, so every fetch destroyed
    // and recreated the v-data-table. Vuetify's data table emits its own
    // `update:options` on mount to normalize the synced options object
    // (filling in sortBy/sortDesc/groupBy/etc.) — that emission mutated
    // cashOptions, which re-triggered this watcher, which fetched again,
    // which flipped loading back on, which unmounted the table again... an
    // infinite fetch loop. The table now stays mounted after its first load
    // (see hasLoadedCash / the v-else branch in the template), so this
    // watcher only fires from a real user page/per-page change — but guard
    // it anyway by ignoring an emission that didn't actually change the
    // page or items-per-page, in case some other Vuetify version re-emits
    // options on other internal updates (e.g. items changing).
    cashOptions: {
      handler(next, prev) {
        if (this.suppressCashOptionsWatch) {
          this.suppressCashOptionsWatch = false;
          return;
        }
        if (
          prev &&
          next.page === prev.page &&
          next.itemsPerPage === prev.itemsPerPage
        ) {
          return;
        }
        this.fetchCashTransactions();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* ---- shift detail dialog ---- */
.shift-detail {
  padding: 4px 2px;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-header-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.detail-header-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #94a3b8;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 20px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 14px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.detail-item--full {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  overflow-wrap: anywhere;
}

.detail-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
}

.detail-stats-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.detail-stat {
  border-radius: 12px;
  background: #eaf3ff;
  padding: 10px 8px;
  text-align: center;
}

.detail-stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #1d4ed8;
  line-height: 1.3;
}

.detail-stat-label {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}

.detail-stat--positive .detail-stat-value {
  color: #15803d;
}

.detail-stat--negative {
  background: #fdecec;
}

.detail-stat--negative .detail-stat-value {
  color: #b3261e;
}

.detail-empty {
  color: #94a3b8;
  font-size: 13px;
}

.detail-empty--block {
  padding: 24px 0;
  text-align: center;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 12px;
}

/* Segmented control replacing the default underlined v-tabs */
.segmented-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 12px;
  margin-bottom: 12px;
}

.segmented-tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.segmented-tab--active {
  background: #ffffff;
  color: #1d4ed8;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.transactions-panel {
  background: transparent;
}

.transactions-table ::v-deep .v-data-table__wrapper {
  border: 1px solid #eef1f5;
  border-radius: 12px;
}

.sale-items-count {
  font-size: 12px;
  font-weight: 600;
  color: #1d4ed8;
}

.sale-items-cell {
  background: #f8fafc;
  padding: 10px 16px !important;
}

.sale-currency-tag {
  margin-left: 4px;
  font-size: 12px;
  color: #64748b;
}

.sale-currency-note {
  margin-bottom: 8px;
  font-size: 11px;
  color: #a95c05;
}

.sale-items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.sale-items-table th {
  text-align: left;
  font-weight: 600;
  color: var(--app-table-header-text);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 10px;
  padding: 4px 8px;
}

.sale-items-table td {
  padding: 6px 8px;
  color: #1f2937;
  border-top: 1px solid #eef1f5;
}

.sale-items-table th.sale-items-num,
.sale-items-table td.sale-items-num {
  text-align: right;
}

.sale-items-summary-row td {
  color: #64748b;
  border-top: 1px dashed #eef1f5;
}

.sale-items-summary-row--total td {
  color: #0f172a;
  font-weight: 700;
  border-top: 1px solid #cbd5e1;
}

@media (max-width: 480px) {
  .detail-grid,
  .detail-stats-row {
    grid-template-columns: 1fr;
  }
}

.theme--dark .detail-header-name,
.theme--dark .detail-section-title,
.theme--dark .sale-items-summary-row--total td {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .detail-header-sub,
.theme--dark .detail-label,
.theme--dark .detail-empty {
  color: rgba(255, 255, 255, 0.5);
}

.theme--dark .detail-value,
.theme--dark .sale-items-table td {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .detail-grid,
.theme--dark .detail-empty--block {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .segmented-tabs {
  background: rgba(255, 255, 255, 0.06);
}

.theme--dark .segmented-tab {
  color: rgba(255, 255, 255, 0.6);
}

.theme--dark .segmented-tab--active {
  background: rgba(255, 255, 255, 0.12);
  color: #90caf9;
}

.theme--dark .shift-detail .transactions-table ::v-deep .v-data-table__wrapper {
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .sale-items-cell {
  background: rgba(255, 255, 255, 0.04);
}

.theme--dark .sale-currency-tag,
.theme--dark .sale-items-summary-row td {
  color: rgba(255, 255, 255, 0.5);
}

.theme--dark .sale-items-table td {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .sale-items-summary-row td {
  border-top-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .sale-items-summary-row--total td {
  border-top-color: rgba(255, 255, 255, 0.24);
}

.theme--dark .detail-stat {
  background: rgba(29, 78, 216, 0.16);
}

.theme--dark .detail-stat--negative {
  background: rgba(179, 38, 30, 0.16);
}

.theme--dark .detail-stat-label {
  color: rgba(255, 255, 255, 0.6);
}
</style>
